import {
    ChangeDetectionStrategy,
    Component,
    EventEmitter,
    Input,
    Output,
    computed,
    inject,
    input,
    signal,
} from '@angular/core'
import { finalize } from 'rxjs'
import { CurrencyPipe, DatePipe } from '@angular/common'
import { PostListItem } from 'src/app/shared/interfaces/post/post-list-item.interface'
import { RouterModule } from '@angular/router'
import { TruncatePipe } from 'src/app/shared/pipes/truncate.pipe'
import { ButtonComponent } from '../../../../../shared/components/form-elements/button/button.component'
import { FontAwesomeModule } from '@fortawesome/angular-fontawesome'
import { NgbDropdownModule, NgbTooltipModule } from '@ng-bootstrap/ng-bootstrap'
import { NgSelectModule } from '@ng-select/ng-select'
import { NgOptionComponent } from '@ng-select/ng-select'
import { PostsStore } from '../../posts.store'
import { StaffListItem } from 'src/app/shared/interfaces/staff/staff-list-item.interface'
import { FormsModule } from '@angular/forms'
import { environment } from 'src/environments/environment'
import { ConfirmationDialogService } from 'src/app/shared/components/confirmation-dialog/confirmation-dialog.service'
import { NgxPermissionsModule } from 'ngx-permissions'
import { AppStore } from 'src/app/core/app.store'
import { NotesDirective } from 'src/app/shared/components/notes/notes.directive'
import { StaffSelectOptionComponent } from 'src/app/shared/components/staff/staff-select-option/staff-select-option.component'
import { PostService } from 'src/app/core/services/posts/post.service'
import { ImageGalleryService } from 'src/app/shared/components/image-gallery/image-gallery.service'
import { GalleryImage } from 'src/app/shared/interfaces/image-gallery/gallery-image.interface'
import { PostFileType } from 'src/app/shared/interfaces/post/post-file.interface'

@Component({
    selector: 'sf-post-detail',
    imports: [
        FormsModule,
        RouterModule,
        FontAwesomeModule,
        NgbTooltipModule,
        NgbDropdownModule,
        NgSelectModule,
        NgOptionComponent,
        NgxPermissionsModule,
        ButtonComponent,
        NotesDirective,
        TruncatePipe,
        DatePipe,
        CurrencyPipe,
        StaffSelectOptionComponent,
    ],
    templateUrl: './post-detail.component.html',
    styleUrls: ['./post-detail.component.scss'],
    changeDetection: ChangeDetectionStrategy.OnPush,
})
export class PostDetailComponent {
    private confirmationService = inject(ConfirmationDialogService)
    appStore = inject(AppStore)
    postStore = inject(PostsStore)
    #postService = inject(PostService)
    #imageGalleryService = inject(ImageGalleryService)

    legacyUrl = environment.legacyUrl

    loadingImages = signal(false)

    @Input() post!: PostListItem
    staff = input<StaffListItem[]>()

    staffMap = computed(() => {
        const map = new Map<number, StaffListItem>()
        this.staff()?.forEach((staff) => map.set(staff.id, staff))
        return map
    })

    @Output() onPostClick = new EventEmitter<PostListItem>()
    @Output() onViewCertificate = new EventEmitter<PostListItem>()

    postClick(): void {
        this.onPostClick.emit(this.post)
    }

    viewCertificate() {
        this.onViewCertificate.emit(this.post)
    }

    assignPostToStaff(staff: StaffListItem): void {
        if (staff) {
            this.postStore.assignPostToStaffId({ postId: this.post.id, staffId: staff.id })
        } else {
            this.postStore.unassignPost(this.post.id)
        }
    }

    takePost() {
        this.postStore.assignPostToStaffId({ postId: this.post.id, staffId: this.appStore.me()!.id })
    }

    unTakePost() {
        this.postStore.unassignPost(this.post.id)
    }

    openImages(): void {
        this.loadingImages.set(true)
        this.#postService
            .getPostImagesByPostId(this.post.id)
            .pipe(finalize(() => this.loadingImages.set(false)))
            .subscribe((files) => {
                // The count on the badge includes videos, the gallery only shows images
                const images: GalleryImage[] = files
                    .filter((x) => x.fileType === PostFileType.Image)
                    .map((x) => ({ id: x.id, url: x.fullUrl, title: x.notes || x.originalFilename }))

                this.#imageGalleryService.open(images, { title: `Post ${this.post.id} images` })
            })
    }

    deletePost(): void {
        this.confirmationService.confirm('Are you sure?', 'Deleting this post cannot be undone').then((result) => {
            if (result) {
                this.postStore.deletePost(this.post.id)
            }
        })
    }
}
